import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { applyTheme } from "../lib/theme.js";
import { PublicDocumentLayout, PublicMessage } from "../components/public/PublicDocumentLayout.jsx";
import { ScopeTable } from "../components/public/ScopeTable.jsx";
import { AcceptanceForm } from "../components/public/AcceptanceForm.jsx";

/**
 * /accept-proposal?token=… — public, no session (4b-3). States: loading,
 * invalid (404), expired (410), accepted (409 or after success), review.
 */
const fmtMoney = (v) => `$${Number(v ?? 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
// Instants (sentAt, acceptedAt) display in the viewer's local time.
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : "");
// Date-only fields (expiresAt) are stored at UTC midnight and must display as
// that UTC calendar date — rendering in local time would shift the date back
// a day for viewers west of UTC.
const fmtDateOnly = (d) => (d ? new Date(d).toLocaleDateString(undefined, { timeZone: "UTC", year: "numeric", month: "long", day: "numeric" }) : "");

export function AcceptProposal() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [state, setState] = useState({ kind: "loading" });
  const [busy, setBusy] = useState(false);
  const [submitErr, setSubmitErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setState({ kind: "invalid" }); return; }
      try {
        const body = await apiFetch(`/api/public/proposals/${encodeURIComponent(token)}`);
        if (cancelled) return;
        applyTheme(body.proposal.accentColor);
        setState({ kind: "review", proposal: body.proposal });
      } catch (e) {
        if (cancelled) return;
        if (e.status === 410) setState({ kind: "expired", expiresAt: e.details?.expiresAt });
        else if (e.status === 409) setState({ kind: "accepted", acceptedAt: e.details?.acceptedAt });
        else if (e.status === 429) setState({ kind: "busy" });
        else setState({ kind: "invalid" });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  async function accept(form) {
    setBusy(true); setSubmitErr(null);
    try {
      const r = await apiFetch(`/api/public/proposals/${encodeURIComponent(token)}`, { method: "POST", body: JSON.stringify(form) });
      setState((s) => ({ kind: "done", name: form.name, acceptedAt: r.acceptedAt, proposal: s.proposal }));
    } catch (e) {
      if (e.status === 409) setState({ kind: "accepted", acceptedAt: e.details?.acceptedAt });
      else if (e.status === 410) setState({ kind: "expired", expiresAt: e.details?.expiresAt });
      else setSubmitErr(e);
    } finally { setBusy(false); }
  }

  const p = state.proposal;
  const branding = p ? { divisionName: p.divisionName, logoUrl: p.logoUrl, accentColor: p.accentColor } : null;

  return (
    <PublicDocumentLayout branding={branding}>
      {state.kind === "loading" && <PublicMessage title="Loading proposal…"> </PublicMessage>}
      {state.kind === "invalid" && (
        <PublicMessage title="This link is not valid">It may have been replaced by a newer proposal. Contact Bayshore Marketing Group for a new link.</PublicMessage>
      )}
      {state.kind === "busy" && <PublicMessage title="Too many requests">Please wait a few minutes and open the link again.</PublicMessage>}
      {state.kind === "expired" && (
        <PublicMessage title="This proposal has expired">{state.expiresAt ? `It was valid until ${fmtDateOnly(state.expiresAt)}. ` : ""}Reply to the email you received to request an updated proposal.</PublicMessage>
      )}
      {state.kind === "accepted" && (
        <PublicMessage title="Already accepted">{state.acceptedAt ? `You accepted this proposal on ${fmtDate(state.acceptedAt)}. ` : ""}Thank you — nothing more is needed.</PublicMessage>
      )}
      {state.kind === "done" && (
        <PublicMessage title={`Thank you, ${state.name}`}>{p.divisionName} will be in touch shortly. Accepted {fmtDate(state.acceptedAt)}.</PublicMessage>
      )}
      {state.kind === "review" && (
        <div className="space-y-6">
          <div>
            <div className="text-[10px] uppercase font-medium text-text-muted" style={{ letterSpacing: "0.5px" }}>Proposal</div>
            <h1 className="text-[20px] font-semibold text-text-primary mt-0.5">{p.title}</h1>
            <p className="text-[12px] text-text-secondary mt-1">
              Prepared for <strong>{p.partyName}</strong> by {p.divisionName}{p.sentAt ? ` · ${fmtDate(p.sentAt)}` : ""}{p.expiresAt ? ` · valid until ${fmtDateOnly(p.expiresAt)}` : ""}
            </p>
            <a href={`/api/public/proposals/${encodeURIComponent(token)}/pdf`} target="_blank" rel="noopener noreferrer" className="inline-block mt-2 text-[12px] text-accent hover:underline">View PDF</a>
          </div>

          <section>
            <h2 className="text-[13px] font-semibold text-text-primary mb-2">Scope of work</h2>
            <ScopeTable lineItems={p.lineItems} />
          </section>

          <section className="rounded-card border border-border-default p-4 text-[13px] space-y-1.5" style={{ fontFeatureSettings: "'tnum'" }}>
            <h2 className="text-[13px] font-semibold text-text-primary mb-1">Investment summary</h2>
            {p.totals.setup > 0 && <div className="flex justify-between"><span className="text-text-secondary">One-time setup</span><span className="font-medium">{fmtMoney(p.totals.setup)}</span></div>}
            {p.totals.monthly > 0 && <div className="flex justify-between"><span className="text-text-secondary">Monthly recurring</span><span className="font-medium">{fmtMoney(p.totals.monthly)}/mo</span></div>}
            {p.totals.annual > 0 && <div className="flex justify-between"><span className="text-text-secondary">Annual recurring</span><span className="font-medium">{fmtMoney(p.totals.annual)}/yr</span></div>}
            {p.totals.setup === 0 && p.totals.monthly === 0 && p.totals.annual === 0 && <div className="text-text-muted">See line items for pricing.</div>}
          </section>

          {p.notes && (
            <section>
              <h2 className="text-[13px] font-semibold text-text-primary mb-1">Terms</h2>
              <p className="text-[13px] text-text-secondary whitespace-pre-line leading-relaxed">{p.notes}</p>
            </section>
          )}

          <section className="border-t border-border-light pt-5">
            <h2 className="text-[13px] font-semibold text-text-primary mb-2">Accept this proposal</h2>
            <AcceptanceForm onSubmit={accept} busy={busy} error={submitErr} />
          </section>
        </div>
      )}
    </PublicDocumentLayout>
  );
}
